<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <b-card-title>
                    {{ $t('org.billing.Weekly Cost') }}
                </b-card-title>
            </div>
            <b-card-actions-container
                v-if="!editMode && data.length && !viewTypeIsMobile"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>
        <template v-if="data.length">
            <div class="position-relative" :style="{ height: 'calc(100% - 78px)'}">
                <WeeklyHeatmapChart
                    ref="container"
                    :svg="svg"
                    :data="data"
                    :weeks="weeks"
                    :dataIfSelected="dataIfSelected"
                    :weekSelected="weekSelected"
                    @select:week="selectWeek"
                    @resize:svg="handleResize"
                    @detail-layer="detailLayer"
                ></WeeklyHeatmapChart>
                <div
                    ref="REF_COLLAPSE"
                    :style="collapseResponsiveStyle"
                >
                    <WeeklyHeatmapTable
                        v-show="!editMode && isVisible"
                        :weeks="weeks"
                        :data="data"
                        @detail-layer="detailLayer"
                    ></WeeklyHeatmapTable>
                </div>
            </div>

        </template>
        <template v-else>
            <div class="d-flex justify-content-center align-items-center flex-column text-center">
                <SVGEmptyIcon/>
                <span>{{ $t('org.dashboard.No data') }}</span>
            </div>
        </template>
    </b-overlay>
</template>

<script>
import BCardActionsContainer from '@/components/BCardActionsContainer'
import WeeklyHeatmapChart from './WeeklyHeatmapChart'
import WeeklyHeatmapTable from './WeeklyHeatmapTable'
import { ref, toRefs, computed, getCurrentInstance, watch } from '@vue/composition-api'
import SVGEmptyIcon from '@/assets/images/empty.svg'
import useDashboard from '@/composables/useDashboard'
import useDashboardRender from '@/composables/useDashboardRender'
export default {
    components: {
        BCardActionsContainer,
        WeeklyHeatmapChart,
        WeeklyHeatmapTable,
        SVGEmptyIcon
    },
    props: {
        editMode: {
            type: Boolean,
            default: false
        },
        isOpen: {
            type: Boolean,
            default: false
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { isOpen } = toRefs(props)
        const { viewType } = useDashboardRender()
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }
        const isVisible = ref(false)
        const tableHeight = ref(null)
        const REF_COLLAPSE = ref(null)
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'summary-card-weeks', status })
        }
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            vm.$nextTick(() => {
                tableHeight.value = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                handleResize()
            })
        })
        const { getSummaryWeeks } = useDashboard()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, summaryWeeks] = getSummaryWeeks(currentParams.value)
        const data = computed(() => summaryWeeks.value.weeks || [])
        const svg = ref({
            width: 0,
            height: 0,
            paddingL: 10 + (1.7 * 14),
            paddingR: 10 + (1.7 * 14),
            paddingT: 25,
            paddingB: 17 + (1.7 * 14)
        })
        const weekSelected = ref(null)
        const dataIfSelected = computed(() => {
            return data.value.filter(item => item.week === weekSelected.value || !weekSelected.value)
        })
        const weeks = computed(() => {
            return dataIfSelected.value.map(item => item.week).filter((obj, pos, arr) => {
                return arr.indexOf(obj) === pos
            }).filter(item => !weekSelected.value || item === weekSelected.value)
        })
        const detailLayer = (date) => context.emit('detail-layer', { date })
        const container = ref(null)
        const handleResize = () => {
            if (container.value) {
                svg.value.width = container.value.$el.clientWidth
                svg.value.height = container.value.$el.clientHeight - tableHeight.value
            }
        }
        const selectWeek = (week) => {
            weekSelected.value = weekSelected.value === week ? null : week
        }
        watch(currentParams, (value, oldValue) => {
            getSummaryWeeks(value)
        }, {
            deep: true
        })
        return {
            REF_COLLAPSE,
            status,
            data,
            svg,
            container,
            weekSelected,
            dataIfSelected,
            weeks,
            detailLayer,
            handleResize,
            selectWeek,
            isVisible,
            triggerCollapse,
            collapseResponsiveStyle,
            viewTypeIsMobile
        }
    }
}
</script>

<style>
#containerWeeklyHeatmap text.shadow {
    text-shadow: #FFF 2px 2px 10px
}
#containerWeeklyHeatmap .list-enter-active, #containerWeeklyHeatmap .list-leave-active {
    transition: all 1s;
}

#containerWeeklyHeatmap .list-enter {
    opacity: 0;
    transform: translateY(30px);
}

#containerWeeklyHeatmap .list-leave-to  {
    opacity: 0;
    transform: translateX(30px);
}
</style>
